/**
 * Componente contenedor principal del mapa
 * @module components/map/MapContainer
 */

import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Crosshair, Loader2 } from 'lucide-react'
import { MAP_CONFIG } from '@/constants'
import { useNodeStore, useUIStore, useFiberStore, useFolderStore, useGeoStore, type MapView } from '@/stores'
import { filterNodesByFolderView } from '@/constants/folderFilter'
import { NodeMarkers } from './NodeMarkers'
import { MapBulkActions } from './MapBulkActions'
import { CableLines } from './CableLines'
import { TracePath } from './TracePath'
import { NodeFormModal, CableFormModal } from '@/components/forms'
import { Coordinates, NodeType } from '@/types/nodes'
import { calcularLongitudRuta } from '@/utils/calcularLongitudRuta'
import { useGeolocation } from '@/hooks/useGeolocation'

// Nodos donde el cable puede terminar/comenzar (puntos de corte)
const NODOS_TERMINALES: NodeType[] = ['NAP', 'CIERRE', 'POP', 'OLT', 'CLIENTE']

// Nodos de paso (el cable pasa pero no se corta)
const NODOS_PASO: NodeType[] = ['POSTE']

// Fix para iconos de Leaflet en Vite
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

L.Marker.prototype.options.icon = DefaultIcon

interface MapContainerProps {
  onMapReady?: (map: L.Map) => void
}

type BaseLayerSet = Record<MapView, L.TileLayer>

export function MapContainer({ onMapReady }: MapContainerProps) {
  const mapRef = useRef<L.Map | null>(null)
  const baseLayersRef = useRef<BaseLayerSet | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const allNodes = useNodeStore((state) => state.nodes)
  const allCables = useFiberStore((state) => state.cables)
  const updateCableStore = useFiberStore((state) => state.updateCable)
  const mode = useUIStore((state) => state.mode)
  const setMode = useUIStore((state) => state.setMode)
  const editingCableId = useUIStore((state) => state.editingCableId)
  const setEditingCableId = useUIStore((state) => state.setEditingCableId)
  const mapSelectMode = useUIStore((state) => state.mapSelectMode)
  const activeFolderId = useFolderStore((state) => state.activeFolderId)

  // Filtrar nodos por carpeta activa (incl. vista "sin carpeta")
  const nodes = filterNodesByFolderView(allNodes, activeFolderId)

  // Filtrar cables: solo mostrar cables cuyos nodos origen Y destino están en la vista actual
  const nodeIdsInView = new Set(nodes.map(n => n.id))
  const cables = allCables.filter((cable: any) => {
    const origenId = cable.origenId || cable.origen
    const destinoId = cable.destinoId || cable.destino
    return nodeIdsInView.has(origenId) && nodeIdsInView.has(destinoId)
  })

  const [isNodeFormOpen, setIsNodeFormOpen] = useState(false)
  const [clickedCoordinates, setClickedCoordinates] = useState<Coordinates | undefined>()

  // Estado para modo addCable con rutas
  const [isCableFormOpen, setIsCableFormOpen] = useState(false)
  const [cableRoute, setCableRoute] = useState<{
    origen: string | null
    postes: string[]
    destino: string | null
  }>({ origen: null, postes: [], destino: null })
  const [temporaryLineLayer, setTemporaryLineLayer] = useState<L.Polyline | null>(null)
  const addNotification = useUIStore((state) => state.addNotification)
  const mapView = useUIStore((state) => state.mapView)

  // GPS overlay / accuracy circle
  const geoFix = useGeoStore((s) => s.lastFix)
  const followOnNext = useGeoStore((s) => s.followOnNext)
  const setGeoFix = useGeoStore((s) => s.setLastFix)
  const setFollowOnNext = useGeoStore((s) => s.setFollowOnNext)
  const accuracyLayerRef = useRef<L.LayerGroup | null>(null)
  const { getCurrentPosition, loading: gpsLoading } = useGeolocation()

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    // Inicializar mapa
    const map = L.map(containerRef.current, {
      center: [MAP_CONFIG.DEFAULT_CENTER.lat, MAP_CONFIG.DEFAULT_CENTER.lng],
      zoom: MAP_CONFIG.DEFAULT_ZOOM,
      minZoom: MAP_CONFIG.MIN_ZOOM,
      maxZoom: MAP_CONFIG.MAX_ZOOM,
      zoomControl: false, // Lo agregamos manualmente después
    })

    const streetLayer = L.tileLayer(MAP_CONFIG.TILE_LAYER, {
      attribution: MAP_CONFIG.ATTRIBUTION,
      maxZoom: MAP_CONFIG.MAX_ZOOM,
    })

    const satelliteLayer = L.tileLayer(MAP_CONFIG.SATELLITE_TILE_LAYER, {
      attribution: MAP_CONFIG.SATELLITE_ATTRIBUTION,
      maxZoom: 19,
    })

    const terrainLayer = L.tileLayer(MAP_CONFIG.TERRAIN_TILE_LAYER, {
      attribution: MAP_CONFIG.TERRAIN_ATTRIBUTION,
      maxZoom: MAP_CONFIG.MAX_ZOOM,
      subdomains: 'abcd',
    })

    const layerByView: BaseLayerSet = {
      street: streetLayer,
      satellite: satelliteLayer,
      terrain: terrainLayer,
    }

    baseLayersRef.current = layerByView

    const savedView = useUIStore.getState().mapView
    const initialLayer = layerByView[savedView] ?? streetLayer
    initialLayer.addTo(map)

    const layerToView = new Map<L.Layer, MapView>([
      [streetLayer, 'street'],
      [satelliteLayer, 'satellite'],
      [terrainLayer, 'terrain'],
    ])

    L.control
      .layers(
        {
          'Calle (OpenStreetMap)': streetLayer,
          Satélite: satelliteLayer,
          'Calle (Voyager)': terrainLayer,
        },
        undefined,
        { position: 'topright' }
      )
      .addTo(map)

    map.on('baselayerchange', (e: L.LayersControlEvent) => {
      const v = layerToView.get(e.layer)
      if (v) useUIStore.getState().setMapView(v)
    })

    // Agregar control de zoom en posición personalizada
    L.control
      .zoom({
        position: 'topright',
      })
      .addTo(map)

    // Agregar control de escala
    L.control
      .scale({
        position: 'bottomleft',
        imperial: false,
        metric: true,
      })
      .addTo(map)

    mapRef.current = map

    // Handler de click en el mapa para agregar nodos
    map.on('click', (e: L.LeafletMouseEvent) => {
      const currentMode = useUIStore.getState().mode
      if (useUIStore.getState().mapSelectMode === 'rect') {
        return
      }

      if (currentMode === 'addNode') {
        // Abrir modal para crear nodo
        setClickedCoordinates({
          lat: e.latlng.lat,
          lng: e.latlng.lng,
        })
        setIsNodeFormOpen(true)
      }
      // TODO: Implementar modo addCable
    })

    // Notificar que el mapa está listo
    if (onMapReady) {
      onMapReady(map)
    }

    // Cleanup
    return () => {
      baseLayersRef.current = null
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [onMapReady])

  // Sincronizar capa base si mapView cambia tras rehidratación de Zustand persist u otra UI
  useEffect(() => {
    const map = mapRef.current
    const layers = baseLayersRef.current
    if (!map || !layers) return

    const target = layers[mapView]
    if (!target || map.hasLayer(target)) return

    ;(Object.values(layers) as L.TileLayer[]).forEach((layer) => {
      if (map.hasLayer(layer)) map.removeLayer(layer)
    })
    target.addTo(map)
  }, [mapView])

  // Ajustar tamaño del mapa cuando cambie el contenedor
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current?.invalidateSize()
      }, 100)
    }
  }, [])

  // Render del pin GPS + círculo de precisión en el mapa
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (accuracyLayerRef.current) {
      accuracyLayerRef.current.clearLayers()
      map.removeLayer(accuracyLayerRef.current)
      accuracyLayerRef.current = null
    }

    if (!geoFix) return

    const group = L.layerGroup().addTo(map)

    // Círculo de incertidumbre
    L.circle([geoFix.lat, geoFix.lng], {
      radius: Math.max(1, geoFix.accuracy),
      color: '#10b981',
      weight: 1,
      fillColor: '#10b981',
      fillOpacity: 0.15,
      interactive: false,
    }).addTo(group)

    // Pin central
    L.circleMarker([geoFix.lat, geoFix.lng], {
      radius: 6,
      color: '#ffffff',
      weight: 2,
      fillColor: '#10b981',
      fillOpacity: 1,
      interactive: false,
    }).addTo(group)

    accuracyLayerRef.current = group

    if (followOnNext) {
      map.setView([geoFix.lat, geoFix.lng], Math.max(map.getZoom(), 18), { animate: true })
      setFollowOnNext(false)
    }

    return () => {
      if (accuracyLayerRef.current) {
        accuracyLayerRef.current.clearLayers()
        map.removeLayer(accuracyLayerRef.current)
        accuracyLayerRef.current = null
      }
    }
  }, [geoFix, followOnNext, setFollowOnNext])

  const handleLocateMe = async () => {
    try {
      const fix = await getCurrentPosition({ highAccuracy: true, timeoutMs: 20000 })
      setGeoFix(fix)
      setFollowOnNext(true)
      addNotification(
        fix.accuracy > 30 ? 'warning' : 'info',
        `Ubicación GPS: ±${Math.round(fix.accuracy)} m`
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo obtener ubicación'
      addNotification('error', msg)
    }
  }

  // Evitar solapar modo rectángulo con agregar nodo/cable/edición de ruta
  useEffect(() => {
    if (mode === 'addNode' || mode === 'addCable' || mode === 'editCableRoute') {
      useUIStore.getState().setMapSelectMode('navigate')
    }
  }, [mode])

  // Escape: salir del modo selección por rectángulo
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && useUIStore.getState().mapSelectMode === 'rect') {
        useUIStore.getState().setMapSelectMode('navigate')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Selección rectangular (arrastrar en el mapa)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    let rectLayer: L.Rectangle | null = null
    let startLL: L.LatLng | null = null
    let active = false

    const paint = (a: L.LatLng, b: L.LatLng) => {
      const bounds = L.latLngBounds([a, b])
      if (!rectLayer) {
        rectLayer = L.rectangle(bounds, {
          color: '#f97316',
          weight: 2,
          fillOpacity: 0.12,
        }).addTo(map)
      } else {
        rectLayer.setBounds(bounds)
      }
    }

    const onMapMouseDown = (ev: L.LeafletMouseEvent) => {
      if (useUIStore.getState().mapSelectMode !== 'rect') return
      const t = ev.originalEvent.target as HTMLElement
      if (t.closest?.('.leaflet-marker-icon')) return
      if (ev.originalEvent.button !== 0) return
      startLL = ev.latlng
      active = true
      map.dragging.disable()
    }

    const onDocMove = (e: MouseEvent) => {
      if (!active || !startLL) return
      if (useUIStore.getState().mapSelectMode !== 'rect') return
      const cur = map.mouseEventToLatLng(e)
      paint(startLL, cur)
    }

    const onDocUp = (e: MouseEvent) => {
      if (!active || !startLL) return
      active = false
      map.dragging.enable()

      const endLL = map.mouseEventToLatLng(e)
      const p0 = map.latLngToContainerPoint(startLL)
      const p1 = map.latLngToContainerPoint(endLL)
      const dist = Math.hypot(p0.x - p1.x, p0.y - p1.y)

      if (rectLayer) {
        map.removeLayer(rectLayer)
        rectLayer = null
      }

      if (useUIStore.getState().mapSelectMode !== 'rect') {
        startLL = null
        return
      }

      if (dist < 8) {
        startLL = null
        return
      }

      const b = L.latLngBounds([startLL, endLL])
      const ids = nodes.filter((n) => b.contains(L.latLng(n.lat, n.lng))).map((n) => n.id)

      const ui = useUIStore.getState()
      if (e.ctrlKey || e.metaKey) {
        ui.addToMapSelection(ids)
      } else {
        ui.setMapSelection(ids)
      }
      if (ids.length) {
        useNodeStore.getState().selectNode(ids[ids.length - 1]!)
      }
      startLL = null
    }

    map.on('mousedown', onMapMouseDown)
    document.addEventListener('mousemove', onDocMove)
    document.addEventListener('mouseup', onDocUp)

    return () => {
      map.off('mousedown', onMapMouseDown)
      document.removeEventListener('mousemove', onDocMove)
      document.removeEventListener('mouseup', onDocUp)
      if (rectLayer && map.hasLayer(rectLayer)) {
        map.removeLayer(rectLayer)
      }
      map.dragging.enable()
    }
  }, [mapSelectMode, nodes])

  // Limpiar selección de nodos cuando se cambia de modo (excluye trazado y edición de ruta)
  useEffect(() => {
    if (mode !== 'addCable' && mode !== 'editCableRoute') {
      setCableRoute({ origen: null, postes: [], destino: null })
      // Limpiar línea temporal
      if (temporaryLineLayer && mapRef.current) {
        mapRef.current.removeLayer(temporaryLineLayer)
        setTemporaryLineLayer(null)
      }
    }
  }, [mode, temporaryLineLayer])

  // Precargar ruta existente al entrar en modo editCableRoute
  useEffect(() => {
    if (mode !== 'editCableRoute' || !editingCableId) return
    const cable = allCables.find((c: any) => c.id === editingCableId)
    if (!cable) return
    const origenId = (cable as any).origenId || (cable as any).origen
    const destinoId = (cable as any).destinoId || (cable as any).destino
    const postesIds: string[] = (cable as any).postesIds
      || ((cable as any).ruta || [])
          .filter((p: any) => p.tipo === 'poste' && p.nodeId)
          .map((p: any) => p.nodeId)
    setCableRoute({
      origen: origenId || null,
      postes: Array.isArray(postesIds) ? [...postesIds] : [],
      destino: destinoId || null,
    })
  }, [mode, editingCableId, allCables])

  // Dibujar línea temporal cuando se está trazando o editando la ruta
  useEffect(() => {
    if (!mapRef.current || (mode !== 'addCable' && mode !== 'editCableRoute')) return

    // Limpiar línea anterior
    if (temporaryLineLayer) {
      mapRef.current.removeLayer(temporaryLineLayer)
    }

    // Construir array de coordenadas
    const routeCoords: L.LatLngExpression[] = []
    
    if (cableRoute.origen) {
      const origenNode = nodes.find(n => n.id === cableRoute.origen)
      if (origenNode) routeCoords.push([origenNode.lat, origenNode.lng])
    }

    cableRoute.postes.forEach(posteId => {
      const posteNode = nodes.find(n => n.id === posteId)
      if (posteNode) routeCoords.push([posteNode.lat, posteNode.lng])
    })

    if (cableRoute.destino) {
      const destinoNode = nodes.find(n => n.id === cableRoute.destino)
      if (destinoNode) routeCoords.push([destinoNode.lat, destinoNode.lng])
    }

    // Crear línea si hay al menos 2 puntos
    if (routeCoords.length >= 1) {
      const line = L.polyline(routeCoords, {
        color: '#10b981',
        weight: 4,
        opacity: 0.8,
        dashArray: '10, 5',
      }).addTo(mapRef.current)
      setTemporaryLineLayer(line)
    }
  }, [cableRoute, mode, nodes])

  // Handler para click en nodo (modo addCable o editCableRoute)
  const handleNodeClickForCable = (nodeId: string) => {
    if (mode !== 'addCable' && mode !== 'editCableRoute') return

    const clickedNode = nodes.find(n => n.id === nodeId)
    if (!clickedNode) return

    const esTerminal = NODOS_TERMINALES.includes(clickedNode.tipo)
    const esPaso = NODOS_PASO.includes(clickedNode.tipo)

    // En modo edición de ruta: origen y destino son fijos, solo se editan postes.
    if (mode === 'editCableRoute') {
      if (!esPaso) {
        addNotification('info', 'En edición solo puedes agregar o quitar postes de la ruta')
        return
      }
      if (cableRoute.postes.includes(nodeId)) {
        setCableRoute({
          ...cableRoute,
          postes: cableRoute.postes.filter(p => p !== nodeId),
        })
        addNotification('info', `Poste ${clickedNode.nombre || ''} removido de la ruta`)
      } else {
        setCableRoute({
          ...cableRoute,
          postes: [...cableRoute.postes, nodeId],
        })
        addNotification('info', `Poste ${clickedNode.nombre || ''} agregado a la ruta`)
      }
      return
    }

    // CASO A: Click en un POSTE → agregar/quitar aunque no haya origen aún
    if (esPaso) {
      if (cableRoute.postes.includes(nodeId)) {
        setCableRoute({
          ...cableRoute,
          postes: cableRoute.postes.filter(p => p !== nodeId)
        })
        addNotification('info', `Poste ${clickedNode.nombre || ''} removido de la ruta`)
      } else {
        setCableRoute({
          ...cableRoute,
          postes: [...cableRoute.postes, nodeId]
        })
        const tip = cableRoute.origen
          ? 'Sigue agregando postes o selecciona destino final.'
          : 'Recuerda seleccionar un nodo terminal (NAP, Cierre, POP, OLT o Cliente) como origen.'
        addNotification('info', `Poste ${clickedNode.nombre || ''} agregado. ${tip}`)
      }
      return
    }

    // CASO B: Click en nodo TERMINAL
    if (!esTerminal) return

    // B.1: Sin origen → fijar origen
    if (!cableRoute.origen) {
      setCableRoute({ ...cableRoute, origen: nodeId })
      addNotification(
        'info',
        `Origen: ${clickedNode.nombre || clickedNode.tipo}. Agrega postes o selecciona destino.`
      )
      return
    }

    // B.2: Click en el mismo nodo de origen → cancelar
    if (cableRoute.origen === nodeId) {
      setCableRoute({ origen: null, postes: [], destino: null })
      addNotification('info', 'Ruta cancelada')
      return
    }

    // B.3: Otro terminal distinto al origen → fijar destino y abrir modal
    setCableRoute({ ...cableRoute, destino: nodeId })
    setIsCableFormOpen(true)
  }

  // Guardar la nueva ruta de un cable editado
  const [savingRoute, setSavingRoute] = useState(false)
  const handleSaveRouteEdit = async () => {
    if (!editingCableId) return
    const cable = allCables.find((c: any) => c.id === editingCableId)
    if (!cable) return

    const origenNode = nodes.find(n => n.id === cableRoute.origen)
    const destinoNode = nodes.find(n => n.id === cableRoute.destino)
    if (!origenNode || !destinoNode) {
      addNotification('error', 'No se encuentran los nodos origen/destino del cable')
      return
    }

    const nuevaRuta: Array<{ lat: number; lng: number; tipo: 'terminal' | 'poste'; nodeId?: string }> = []
    nuevaRuta.push({ lat: origenNode.lat, lng: origenNode.lng, tipo: 'terminal', nodeId: origenNode.id })
    cableRoute.postes.forEach(pid => {
      const p = nodes.find(n => n.id === pid)
      if (p) nuevaRuta.push({ lat: p.lat, lng: p.lng, tipo: 'poste', nodeId: pid })
    })
    nuevaRuta.push({ lat: destinoNode.lat, lng: destinoNode.lng, tipo: 'terminal', nodeId: destinoNode.id })

    const longitudTotal = calcularLongitudRuta(nuevaRuta)

    setSavingRoute(true)
    try {
      await updateCableStore({
        ...(cable as any),
        ruta: nuevaRuta,
        postesIds: [...cableRoute.postes],
        longitudTotal,
      } as any)
      addNotification('success', 'Ruta del cable actualizada')
      setEditingCableId(null)
      setMode('view')
    } catch (err) {
      console.error('Error guardando ruta:', err)
      addNotification('error', 'No se pudo guardar la nueva ruta')
    } finally {
      setSavingRoute(false)
    }
  }

  const handleCancelRouteEdit = () => {
    setEditingCableId(null)
    setMode('view')
  }

  // Obtener todos los nodos seleccionados para visual feedback
  const getSelectedNodesForCable = (): string[] => {
    const selected: string[] = []
    if (cableRoute.origen) selected.push(cableRoute.origen)
    selected.push(...cableRoute.postes)
    if (cableRoute.destino) selected.push(cableRoute.destino)
    return selected
  }

  return (
    <div className="relative h-full w-full">
      {/* Contenedor del mapa */}
      <div ref={containerRef} className="h-full w-full" />

      {/* Overlay de carga si no hay mapa */}
      {!mapRef.current && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-dark-900">
          <div className="text-center">
            <div className="text-4xl mb-2">🗺️</div>
            <p className="text-gray-600 dark:text-gray-400">Cargando mapa...</p>
          </div>
        </div>
      )}

      {/* Renderizar cables */}
      {mapRef.current && <CableLines map={mapRef.current} cables={cables} />}

      {/* Renderizar ruta de trazabilidad */}
      {mapRef.current && <TracePath map={mapRef.current} />}

      {/* Renderizar marcadores de nodos */}
      {mapRef.current && (
        <NodeMarkers
          map={mapRef.current}
          nodes={nodes}
          onNodeClickForCable={
            mode === 'addCable' || mode === 'editCableRoute'
              ? handleNodeClickForCable
              : undefined
          }
          selectedNodesForCable={getSelectedNodesForCable()}
        />
      )}

      <MapBulkActions />

      {/* FAB GPS — visible siempre que haya mapa */}
      <button
        type="button"
        onClick={handleLocateMe}
        disabled={gpsLoading}
        aria-label="Localizarme con GPS"
        className="absolute right-4 bottom-20 md:bottom-16 z-[1000] w-12 h-12 rounded-full shadow-lg bg-white dark:bg-dark-800 text-emerald-600 border border-gray-200 dark:border-dark-600 flex items-center justify-center hover:bg-emerald-50 active:scale-95 transition disabled:opacity-60 touch-manipulation"
        style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
        title="Mi ubicación GPS"
      >
        {gpsLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Crosshair className="w-5 h-5" />
        )}
      </button>

      {/* Overlay de estado GPS */}
      {geoFix && (
        <div
          className={`absolute left-4 bottom-4 z-[999] px-3 py-1.5 rounded-full shadow text-xs font-medium flex items-center gap-2 ${
            geoFix.accuracy > 30
              ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200'
              : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'
          }`}
          style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <Crosshair className="w-3.5 h-3.5" />
          ±{Math.round(geoFix.accuracy)} m
        </div>
      )}

      {/* Info overlay */}
      <div className="absolute bottom-4 right-4 bg-white dark:bg-dark-800 px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-gray-600 dark:text-gray-400">Nodos:</span>
          <span className="font-bold text-primary-600 dark:text-primary-400">
            {nodes.length}
          </span>
        </div>
      </div>

      {/* Cursor indicator en modo addNode */}
      {mode === 'addNode' && (
        <div className="absolute top-36 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-[999]">
          📍 Click en el mapa para agregar nodo
        </div>
      )}

      {mapSelectMode === 'rect' && (
        <div className="absolute top-36 left-1/2 z-[999] max-w-md -translate-x-1/2 rounded-lg border border-amber-400 bg-amber-50 px-4 py-2 text-center text-sm text-amber-950 shadow-lg dark:border-amber-600 dark:bg-amber-950/90 dark:text-amber-100">
          <strong>Selección por área:</strong> arrastra un rectángulo en el mapa. Mantén Ctrl/Cmd al soltar para
          añadir a la selección. Clic en nodo: sustituye selección; Ctrl/Cmd+clic: alterna. Escape: salir.
        </div>
      )}

      {/* Cursor indicator en modo addCable */}
      {mode === 'addCable' && (
        <div className="absolute top-36 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm z-[999] max-w-md text-center">
          <div className="font-medium mb-1">🔗 Trazado de Cable</div>
          <div className="text-xs opacity-90">
            {!cableRoute.origen ? (
              <>
                Selecciona el nodo ORIGEN (NAP, Cierre, POP, OLT, Cliente) o marca postes del trayecto.
                {cableRoute.postes.length > 0 && (
                  <><br />Postes en ruta: {cableRoute.postes.length}</>
                )}
              </>
            ) : !cableRoute.destino ? (
              <>
                ✓ Origen seleccionado
                {cableRoute.postes.length > 0 && ` • ${cableRoute.postes.length} poste(s) de paso`}
                <br />
                Agrega postes de paso o selecciona el nodo DESTINO
              </>
            ) : (
              'Configurando cable...'
            )}
          </div>
        </div>
      )}

      {/* Cursor indicator en modo editCableRoute */}
      {mode === 'editCableRoute' && (
        <div className="absolute top-36 left-1/2 transform -translate-x-1/2 bg-amber-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm z-[999] max-w-md text-center">
          <div className="font-medium mb-1">✏️ Editando ruta del cable</div>
          <div className="text-xs opacity-90 mb-2">
            Click en un poste para agregar/quitar. Origen y destino no cambian.
            <br />
            Postes en ruta: {cableRoute.postes.length}
          </div>
          <div className="flex gap-2 justify-center mt-2">
            <button
              type="button"
              onClick={handleSaveRouteEdit}
              disabled={savingRoute}
              className="px-3 py-1 bg-white text-amber-700 rounded font-medium text-xs hover:bg-amber-50 disabled:opacity-60"
            >
              {savingRoute ? 'Guardando…' : 'Guardar ruta'}
            </button>
            <button
              type="button"
              onClick={handleCancelRouteEdit}
              disabled={savingRoute}
              className="px-3 py-1 bg-amber-800 text-white rounded font-medium text-xs hover:bg-amber-900 disabled:opacity-60"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal de formulario de nodo */}
      <NodeFormModal
        isOpen={isNodeFormOpen}
        onClose={() => {
          setIsNodeFormOpen(false)
          setClickedCoordinates(undefined)
        }}
        coordinates={clickedCoordinates}
      />

      {/* Modal de formulario de cable */}
      <CableFormModal
        isOpen={isCableFormOpen}
        onClose={() => {
          setIsCableFormOpen(false)
          setCableRoute({ origen: null, postes: [], destino: null })
        }}
        origenNodeId={cableRoute.origen || undefined}
        destinoNodeId={cableRoute.destino || undefined}
        postesIntermmedios={cableRoute.postes}
      />
    </div>
  )
}
